import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronRight, FolderKanban, Calendar, Radio } from 'lucide-react';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { Collapsible } from '@/components/ui/Collapsible';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import type { XkEvent, XkEventGroup, XkStation } from '@/lib/api/types';

/**
 * Card-footer collapsible that renders the brand → project → event → station
 * tree on demand. Each level mounts its query ONLY when its parent is open,
 * so a collapsed brand makes zero requests. Every node is a real link, so
 * the user can jump straight to any resource without paging through screens.
 */
export function BrandTreeCollapsible({ brandId }: { brandId: number }) {
  const [open, setOpen] = useState(false);
  const t = useT();
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      ariaLabel={t('brandTree.label')}
    >
      {open && <BrandProjects brandId={brandId} />}
    </Collapsible>
  );
}

// ── Level 1 — projects (xk_event_group filtered by companyId = brandId) ────

function BrandProjects({ brandId }: { brandId: number }) {
  const t = useT();
  const q = useQuery({
    queryKey: queryKeys.eventGroups.byBrand(brandId),
    queryFn: () => eventsApi.listGroups({ companyId: brandId }),
  });
  if (q.isLoading) return <RowSkeleton depth={0} />;
  if (q.isError) return <RowText depth={0} text={t('brandTree.loadError')} />;
  const items = q.data?.items ?? [];
  if (items.length === 0)
    return <RowText depth={0} text={t('brandTree.empty.projects')} />;
  return (
    <ul className="space-y-0.5">
      {items.map((p) => (
        <ProjectNode key={p.id} project={p} />
      ))}
    </ul>
  );
}

function ProjectNode({ project }: { project: XkEventGroup }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <TreeRow
        depth={0}
        icon={<FolderKanban className="h-3.5 w-3.5" />}
        href={`/projects/${project.id}`}
        label={project.name}
        expanded={open}
        onToggle={() => setOpen((o) => !o)}
      />
      {open && <ProjectEvents projectId={project.id} />}
    </li>
  );
}

// ── Level 2 — events (xk_event filtered by eventGroupId) ───────────────────

function ProjectEvents({ projectId }: { projectId: number }) {
  const t = useT();
  const q = useQuery({
    queryKey: queryKeys.events.byGroup(projectId),
    queryFn: () => eventsApi.listEvents({ eventGroupId: projectId }),
  });
  if (q.isLoading) return <RowSkeleton depth={1} />;
  if (q.isError) return <RowText depth={1} text={t('brandTree.loadError')} />;
  const items = q.data?.items ?? [];
  if (items.length === 0)
    return <RowText depth={1} text={t('brandTree.empty.events')} />;
  return (
    <ul className="mt-0.5 space-y-0.5">
      {items.map((e) => (
        <EventNode key={e.id} event={e} projectId={projectId} />
      ))}
    </ul>
  );
}

function EventNode({
  event,
  projectId,
}: {
  event: XkEvent;
  projectId: number;
}) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <TreeRow
        depth={1}
        icon={<Calendar className="h-3.5 w-3.5" />}
        href={`/projects/${projectId}/events/${event.id}`}
        label={event.name}
        expanded={open}
        onToggle={() => setOpen((o) => !o)}
      />
      {open && <EventStations eventId={event.id} />}
    </li>
  );
}

// ── Level 3 — stations (xk_station filtered by eventId) ────────────────────

function EventStations({ eventId }: { eventId: number }) {
  const t = useT();
  const q = useQuery({
    queryKey: queryKeys.stations.byEvent(eventId),
    queryFn: () => eventsApi.listStations({ eventId }),
  });
  if (q.isLoading) return <RowSkeleton depth={2} />;
  if (q.isError) return <RowText depth={2} text={t('brandTree.loadError')} />;
  const items = q.data?.items ?? [];
  if (items.length === 0)
    return <RowText depth={2} text={t('brandTree.empty.stations')} />;
  return (
    <ul className="mt-0.5 space-y-0.5">
      {items.map((s) => (
        <StationLeaf key={s.id} station={s} />
      ))}
    </ul>
  );
}

function StationLeaf({ station }: { station: XkStation }) {
  return (
    <li>
      <TreeRow
        depth={2}
        icon={<Radio className="h-3.5 w-3.5" />}
        href={`/stations/${station.id}`}
        label={station.name}
      />
    </li>
  );
}

// ── Row primitives ─────────────────────────────────────────────────────────

function TreeRow({
  depth,
  icon,
  href,
  label,
  expanded,
  onToggle,
}: {
  depth: 0 | 1 | 2;
  icon: ReactNode;
  href: string;
  label: string;
  expanded?: boolean;
  onToggle?: () => void;
}) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-md py-1 pr-2 transition-colors hover:bg-[var(--color-xk-bg-deep)]"
      style={{ paddingLeft: depth * 14 + 2 }}
    >
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          aria-expanded={expanded}
          className="grid h-4 w-4 flex-none place-items-center rounded text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
        >
          <ChevronRight
            className={cn('h-3 w-3 transition-transform', expanded && 'rotate-90')}
          />
        </button>
      ) : (
        <span className="h-4 w-4 flex-none" />
      )}
      <span className="flex-none text-[var(--color-xk-text-muted)]">{icon}</span>
      <Link
        to={href}
        className="min-w-0 flex-1 truncate text-[12px] font-semibold text-[var(--color-xk-lavender)] hover:text-[var(--color-accent-ink)] hover:underline"
        title={label}
      >
        {label}
      </Link>
    </div>
  );
}

function RowSkeleton({ depth }: { depth: 0 | 1 | 2 }) {
  return (
    <div className="py-1" style={{ paddingLeft: depth * 14 + 22 }}>
      <Skeleton className="h-4 w-32 rounded" />
    </div>
  );
}

function RowText({ depth, text }: { depth: 0 | 1 | 2; text: string }) {
  return (
    <p
      className="py-1 text-[11px] italic text-[var(--color-xk-text-muted)]"
      style={{ paddingLeft: depth * 14 + 22 }}
    >
      {text}
    </p>
  );
}
